import { Button, Card, DropMenu, Header, LoadingError } from "@kaladel/react/Components";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import styles from "./Home.module.scss";
import { useApp } from "../../AppContext";
import { faChartLine, faChevronRight, faCreditCard, faHandHoldingDollar, faHome, faPiggyBank, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Page } from "Components/Page";
import { CreditCardsCard } from "Components/HomeCards/CreditCardsCard";
import { AccountsCard } from "Components/HomeCards/AccountsCard";
import { LoansCard } from "Components/HomeCards/LoansCard";
import { MortgagesCard } from "Components/HomeCards/MortgagesCard";
import { InvestmentsCard } from "Components/HomeCards/InvestmentsCard";
import { faMoneyBillTransfer } from "@fortawesome/free-solid-svg-icons/faMoneyBillTransfer";
import { AddAccountModal } from "Modals/AddAccountModal";
import { LoanAddModal } from "Modals/LoanAddModal";
import { MortgageAddModal } from "Modals/MortgageAddModal";
import { AddCreditCardModal } from "Modals/AddCreditCardModal";
import { Severity } from "@kaladel/react/Utilities";
import { InvestmentAddModal } from "../../Modals/InvestmentAddModal";

export const Home: FC = () => {
    const { activeCurrency, session, status, refreshStatus } = useApp();
    const navigate = useNavigate();

    const [addModalOpen, setAddModalOpen] = useState<"account" | "savings" | "investment" | "creditcard" | "loan" | "mortgage" | null>(null);

    useEffect(() => {
        if (!session?.id) {
            navigate("/auth/start");
        }
    }, [session]);

    const addModalClosed = (dirty: boolean, newAccountId?: string) => {
        if (dirty) {
            refreshStatus();

            if (newAccountId) {
                navigate(`${addModalOpen}s/${newAccountId}`);
            }
        }

        setAddModalOpen(null);
    };

    const activeCurrencyStatus = useMemo(() => {
        return status?.find(s => s.currency.code === activeCurrency);
    }, [activeCurrency]);

    const renderAddModal = () => {
        switch (addModalOpen) {
            case "account":
                return <AddAccountModal onClose={addModalClosed} />;
            case "savings":
                return null;
            case "investment":
                return <InvestmentAddModal onClose={addModalClosed} />;
            case "creditcard":
                return <AddCreditCardModal onClose={addModalClosed} />;
            case "loan":
                return <LoanAddModal onClose={addModalClosed} />;
            case "mortgage":
                return <MortgageAddModal onClose={addModalClosed} />;
            default:
                return null;
        }
    };

    return (
        <>
            <LoadingError
                value={status}
                loaded={status => (
                    <>

                        {!status.length && (
                            <Page title="Welcome!">

                                <Header title="Your first step is to create an account" level={2} />

                                <Button
                                    onClick={() => navigate("accounts")}
                                    icon={faChevronRight}
                                    iconSide="right"
                                >
                                    Get started!
                                </Button>
                            </Page>
                        )}

                        {!!activeCurrencyStatus && (
                            <>
                                <Page
                                    title="Overview"
                                    right={(
                                        <DropMenu
                                            trigger={<Button severity={Severity.Success} icon={faPlus}>Add...</Button>}
                                            direction="left"
                                            items={[
                                                { text: "Current Account", icon: faMoneyBillTransfer, onClick: () => setAddModalOpen("account") },
                                                { text: "Savings Account", icon: faPiggyBank, onClick: () => setAddModalOpen("savings"), disabled: true },
                                                { text: "Investment Account", icon: faChartLine, onClick: () => setAddModalOpen("investment") },
                                                { text: "Credit Card", icon: faCreditCard, onClick: () => setAddModalOpen("creditcard") },
                                                { text: "Loan", icon: faHandHoldingDollar, onClick: () => setAddModalOpen("loan") },
                                                { text: "Mortgage", icon: faHome, onClick: () => setAddModalOpen("mortgage") },
                                            ]}
                                        />
                                    )}
                                >

                                    <div className={styles.panels}>
                                        {activeCurrencyStatus?.hasAccounts && (
                                            <AccountsCard />
                                        )}

                                        {activeCurrencyStatus?.hasInvestmentAccounts && (
                                            <InvestmentsCard />
                                        )}

                                        {activeCurrencyStatus?.hasCreditCards && (
                                            <CreditCardsCard />
                                        )}

                                        {activeCurrencyStatus?.hasLoans && (
                                            <LoansCard />
                                        )}

                                        {activeCurrencyStatus?.hasMortgages && (
                                            <MortgagesCard />
                                        )}

                                        <Card>
                                            <Header title="Tools" />
                                            <div>
                                                <div><Link to="debtforecasts">Debt Forecasts</Link></div>
                                            </div>
                                        </Card>
                                    </div>
                                </Page>

                            </>
                        )}
                    </>
                )}
            />

            {renderAddModal()}
        </>
    );
};
